import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Gift as GiftIcon, Loader2 } from "lucide-react";
import { api, errText, useAuth } from "@/lib/auth";

const PRODUCTS = [["gates", "13 Gates to Ecstasy", "$297"], ["quantum", "Quantum Intimacy", "$297"], ["bundle", "Both apps", "$497"]];
const field = "w-full bg-[#0F070B] border border-[#D9B47A]/25 px-4 py-3 text-[16px] text-[#F2E8DF] placeholder:text-[#8F8176] focus:outline-none focus:border-[#D9B47A]";
const gold = "w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D9B47A] text-[#0A0706] py-4 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#F2E8DF] transition-colors disabled:opacity-60";
const Shell = ({ testId, children }) => (
  <main data-testid={testId} className="min-h-screen bg-[#0A0706] text-[#F2E8DF] pt-32 pb-24 px-5">
    <div className="max-w-xl mx-auto">{children}</div>
  </main>
);

const Sent = ({ sid }) => {
  const [state, setState] = useState("checking");
  useEffect(() => {
    let n = 0;
    let alive = true;
    const poll = () => api.get(`/payments/status/${sid}`).then(({ data }) => {
      if (!alive) return;
      if (data?.payment_status === "paid") setState("paid");
      else if (++n < 15) setTimeout(poll, 2000);
      else setState("pending");
    }).catch(() => alive && setState("pending"));
    poll();
    return () => {
      alive = false;
    };
  }, [sid]);
  return (
    <Shell testId="gift-sent">
      <GiftIcon className="w-10 h-10 text-[#D9B47A]" strokeWidth={1.2} />
      <h1 className="mt-6 font-display text-4xl sm:text-5xl leading-tight">{state === "paid" ? <>Your gift is <em className="text-[#D9B47A]">on its way.</em></> : "Confirming your gift…"}</h1>
      <p data-testid="gift-sent-status" className="mt-4 text-[#CFC2B6]">{state === "paid" ? "Ariel has emailed them a personal invitation to open it whenever they are ready." : state === "pending" ? "Payment is still confirming. Ariel will send the gift as soon as it completes." : <Loader2 className="w-5 h-5 animate-spin" />}</p>
    </Shell>
  );
};

const Buy = () => {
  const { user } = useAuth();
  const [form, setForm] = useState({ product: "gates", recipient_name: "", recipient_email: "", from_name: user?.name?.split(" ")[0] ?? "", message: "", buyer_email: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/gifts/checkout", { ...form, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      setBusy(false);
      window.alert(errText(err.response?.data?.detail));
    }
  };
  return (
    <Shell testId="gift-page">
      <p className="font-tt-mono text-[11px] uppercase tracking-[0.3em] text-[#D9B47A]">A gift from the Temple</p>
      <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-tight">Give someone <em className="text-[#D9B47A]">the gates.</em></h1>
      <p className="mt-4 text-[#CFC2B6]">Ariel emails them a personal invitation with your message. They open it whenever they are ready, and it is theirs for life.</p>
      <form onSubmit={submit} className="mt-10 grid gap-4">
        <div role="radiogroup" aria-label="Gift" className="grid gap-2">
          {PRODUCTS.map(([k, l, p]) => (
            <button key={k} type="button" role="radio" aria-checked={form.product === k} data-testid={`gift-product-${k}`} onClick={() => setForm((f) => ({ ...f, product: k }))}
              className={`flex items-center justify-between border px-5 py-4 text-left transition-colors ${form.product === k ? "border-[#D9B47A] bg-[#1A120E]" : "border-[#D9B47A]/20 hover:border-[#D9B47A]/50"}`}>
              <span className="font-display text-xl">{l}</span><span className="font-tt-mono text-[13px] text-[#D9B47A]">{p} AUD</span>
            </button>
          ))}
        </div>
        <input data-testid="gift-recipient-name" required maxLength={80} value={form.recipient_name} onChange={set("recipient_name")} placeholder="Their name" className={field} />
        <input data-testid="gift-recipient-email" required type="email" maxLength={200} value={form.recipient_email} onChange={set("recipient_email")} placeholder="Their email" className={field} />
        <textarea data-testid="gift-message" maxLength={500} rows={4} value={form.message} onChange={set("message")} placeholder="A few words for them (optional)" className={field} />
        <input data-testid="gift-from-name" required maxLength={80} value={form.from_name} onChange={set("from_name")} placeholder="Your name, as it appears on the gift" className={field} />
        {!user && <input data-testid="gift-buyer-email" required type="email" maxLength={200} value={form.buyer_email} onChange={set("buyer_email")} placeholder="Your email, for the receipt" className={field} />}
        <button data-testid="gift-pay" disabled={busy} className={gold}>{busy && <Loader2 className="w-4 h-4 animate-spin" />} Continue to payment</button>
      </form>
    </Shell>
  );
};

// /gift/redeem?code= — the recipient opens their gift (signing in or creating an account).
export function GiftRedeem() {
  const [params] = useSearchParams();
  const code = params.get("code") ?? "";
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [g, setG] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get(`/gifts/${encodeURIComponent(code)}`).then(({ data }) => setG(data ?? { valid: false })).catch(() => setG({ valid: false }));
  }, [code]);
  const open = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/gifts/redeem", { code, ...(user ? {} : form) });
      const me = await api.get("/auth/me").catch(() => null);
      if (me?.data) setUser(me.data);
      navigate(data?.product === "quantum" ? "/quantum" : "/app");
    } catch (err) {
      setError(errText(err.response?.data?.detail));
      setBusy(false);
    }
  };
  if (!g) return <Shell testId="gift-redeem"><Loader2 className="w-5 h-5 animate-spin text-[#D9B47A]" /></Shell>;
  if (!g.valid) return <Shell testId="gift-redeem"><h1 className="font-display text-4xl">This gift link isn’t valid.</h1></Shell>;
  return (
    <Shell testId="gift-redeem">
      <GiftIcon className="w-10 h-10 text-[#D9B47A]" strokeWidth={1.2} />
      <h1 className="mt-6 font-display text-4xl sm:text-5xl leading-tight">{g.recipient_name}, <em className="text-[#D9B47A]">{g.from_name}</em> has given you {g.name}.</h1>
      {g.message && <p data-testid="gift-redeem-message" className="mt-6 border-l-2 border-[#E8838E] pl-5 font-display italic text-xl text-[#F2E8DF]">“{g.message}”</p>}
      {g.redeemed ? <p data-testid="gift-redeemed" className="mt-8 text-[#CFC2B6]">This gift has already been opened. Sign in to enter the Temple.</p> : user ? (
        <button data-testid="gift-redeem-open" onClick={open} disabled={busy} className={`${gold} mt-10`}>{busy && <Loader2 className="w-4 h-4 animate-spin" />} Open your gift</button>
      ) : (
        <form onSubmit={open} className="mt-10 grid gap-3">
          <p className="text-[14px] text-[#A8998C]">Create your account to open it. Already have one? Use the same email and password.</p>
          <input data-testid="gift-redeem-name" maxLength={120} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Your name" className={field} />
          <input data-testid="gift-redeem-email" required type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email" className={field} />
          <input data-testid="gift-redeem-password" required type="password" minLength={8} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="Password (8+ characters)" className={field} />
          <button data-testid="gift-redeem-open" disabled={busy} className={gold}>{busy && <Loader2 className="w-4 h-4 animate-spin" />} Open your gift</button>
        </form>
      )}
      {error && <p data-testid="gift-redeem-error" className="mt-4 text-[#E8838E]">{error}</p>}
    </Shell>
  );
}

// /gift — choose a gift; after Stripe, ?session_id= confirms it was sent.
export default function Gift() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  return sid ? <Sent sid={sid} /> : <Buy />;
}
